import {
  MUSIC_REGISTRY,
  SFX_REGISTRY,
  MusicId,
  SfxId,
} from '../data/audio.config';
import { SfxEntry } from '../types/audio.types';

interface ActiveMusic {
  id: MusicId;
  source: AudioBufferSourceNode;
  gain: GainNode;
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/**
 * Graph architecture:
 *   per-source gain → categoryGain (music or sfx) → masterGain → destination
 *
 * - `per-source gain` drives fades and the asset's base volume.
 * - `categoryGain` is driven by the music / sfx volume sliders.
 * - `masterGain` is driven by the master slider.
 *
 * The audio context is created lazily and resumes automatically on the first
 * user interaction (browser autoplay policy).
 */
interface AudioGraph {
  ctx: AudioContext;
  masterGain: GainNode;
  musicCatGain: GainNode;
  sfxCatGain: GainNode;
}

class AudioService {
  private graph: AudioGraph | null = null;

  private buffers = new Map<string, AudioBuffer>();
  private decoding = new Map<string, Promise<AudioBuffer>>();
  private current: ActiveMusic | null = null;

  private masterVolume = 1;
  private musicVolume = 0.7;
  private sfxVolume = 0.8;

  // Creates the graph lazily. Does NOT resume: decoding works on a suspended
  // context, and resuming before a user gesture is what triggers the autoplay
  // warning (and can wedge the context). Resume happens in `unlock()` and, as a
  // safety net, on the first user gesture (see `armGestureUnlock`).
  async ensureGraph(): Promise<AudioGraph> {
    if (!this.graph) {
      const ctx = new AudioContext();
      const masterGain = ctx.createGain();
      masterGain.gain.value = this.masterVolume;
      masterGain.connect(ctx.destination);
      const musicCatGain = ctx.createGain();
      musicCatGain.gain.value = this.musicVolume;
      musicCatGain.connect(masterGain);
      const sfxCatGain = ctx.createGain();
      sfxCatGain.gain.value = this.sfxVolume;
      sfxCatGain.connect(masterGain);
      this.graph = { ctx, masterGain, musicCatGain, sfxCatGain };
      this.armGestureUnlock(ctx);
    }
    return this.graph;
  }

  // Resume the context — call from a user gesture (boot gate in LoadingScreen).
  // Fire-and-forget so a wedged context can never block boot; the gesture
  // listener armed at creation is the fallback for paths that skip this.
  async unlock(): Promise<void> {
    const { ctx } = await this.ensureGraph();
    if (ctx.state === 'suspended') {
      void ctx.resume().catch(() => {});
    }
  }

  // The context is often created before any gesture (boot-time prewarm decode),
  // so resume on the first gesture regardless of who created it. Self-removes
  // once the context is running.
  private armGestureUnlock(ctx: AudioContext): void {
    const resume = () => {
      void ctx
        .resume()
        .then(() => {
          window.removeEventListener('pointerdown', resume);
          window.removeEventListener('keydown', resume);
        })
        .catch(() => {});
    };
    window.addEventListener('pointerdown', resume);
    window.addEventListener('keydown', resume);
  }

  setMasterVolume(v: number): void {
    this.masterVolume = clamp01(v);
    if (this.graph) {
      this.graph.masterGain.gain.value = this.masterVolume;
    }
  }

  setMusicCategoryVolume(v: number): void {
    this.musicVolume = clamp01(v);
    if (this.graph) {
      this.graph.musicCatGain.gain.value = this.musicVolume;
    }
  }

  setSfxCategoryVolume(v: number): void {
    this.sfxVolume = clamp01(v);
    if (this.graph) {
      this.graph.sfxCatGain.gain.value = this.sfxVolume;
    }
  }

  getMasterVolume(): number {
    return this.masterVolume;
  }
  getMusicVolume(): number {
    return this.musicVolume;
  }
  getSfxVolume(): number {
    return this.sfxVolume;
  }

  /** Eagerly decode a bundle's music + sfx so the first play is instant. Play
   * stays lazy elsewhere; failures are swallowed (best-effort). */
  prewarm(music: MusicId[], sfx: SfxId[]): void {
    const urls = new Set<string>();
    for (const id of music) {
      const e = MUSIC_REGISTRY[id];
      if (e) {
        urls.add(e.src);
      }
    }
    for (const id of sfx) {
      const e = SFX_REGISTRY[id] as SfxEntry | undefined;
      if (!e) {
        continue;
      }
      if ('src' in e) {
        urls.add(e.src);
      } else if ('variants' in e) {
        Object.values(e.variants).forEach((u) => urls.add(u));
      } else if ('pool' in e) {
        e.pool.forEach((u) => urls.add(u));
      }
    }
    urls.forEach((u) => void this.loadBuffer(u).catch(() => {}));
  }

  private async loadBuffer(src: string): Promise<AudioBuffer> {
    const cached = this.buffers.get(src);
    if (cached) {
      return cached;
    }
    const inflight = this.decoding.get(src);
    if (inflight) {
      return inflight;
    }
    const promise = (async () => {
      const { ctx } = await this.ensureGraph();
      const res = await fetch(src);
      const arr = await res.arrayBuffer();
      const buf = await ctx.decodeAudioData(arr);
      this.buffers.set(src, buf);
      this.decoding.delete(src);
      return buf;
    })();
    this.decoding.set(src, promise);
    return promise;
  }

  async playMusic(id: MusicId, fadeMs = 1000): Promise<void> {
    if (this.current?.id === id) {
      return;
    }
    const entry = MUSIC_REGISTRY[id];
    if (!entry) {
      return;
    }

    const graph = await this.ensureGraph();
    const buffer = await this.loadBuffer(entry.src);

    const source = graph.ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;

    const gain = graph.ctx.createGain();
    gain.gain.value = 0;
    source.connect(gain);
    gain.connect(graph.musicCatGain);
    source.start();

    const targetVol = entry.volume ?? 1;
    const now = graph.ctx.currentTime;
    gain.gain.linearRampToValueAtTime(targetVol, now + fadeMs / 1000);

    if (this.current) {
      this.fadeAndStop(this.current, fadeMs);
    }
    this.current = { id, source, gain };
  }

  fadeOutMusic(fadeMs = 1000): void {
    if (!this.current) {
      return;
    }
    this.fadeAndStop(this.current, fadeMs);
    this.current = null;
  }

  private fadeAndStop(m: ActiveMusic, fadeMs: number): void {
    if (!this.graph) {
      return;
    }
    const now = this.graph.ctx.currentTime;
    m.gain.gain.cancelScheduledValues(now);
    m.gain.gain.setValueAtTime(m.gain.gain.value, now);
    m.gain.gain.linearRampToValueAtTime(0, now + fadeMs / 1000);
    setTimeout(() => {
      try {
        m.source.stop();
      } catch {
        // already stopped
      }
      m.source.disconnect();
      m.gain.disconnect();
    }, fadeMs);
  }

  async playSfx(id: SfxId, variant?: string): Promise<void> {
    const entry = SFX_REGISTRY[id] as SfxEntry | undefined;
    if (!entry) {
      return;
    }
    const src = resolveSfxSrc(entry, variant);
    if (!src) {
      return;
    }

    const graph = await this.ensureGraph();
    const buffer = await this.loadBuffer(src);

    const source = graph.ctx.createBufferSource();
    source.buffer = buffer;
    const gain = graph.ctx.createGain();
    gain.gain.value = entry.volume ?? 1;
    source.connect(gain);
    gain.connect(graph.sfxCatGain);
    source.start();

    source.onended = () => {
      source.disconnect();
      gain.disconnect();
    };
  }
}

function resolveSfxSrc(entry: SfxEntry, variant?: string): string | undefined {
  if ('src' in entry) {
    return entry.src;
  }
  if ('variants' in entry) {
    return entry.variants[variant ?? entry.defaultVariant];
  }
  return entry.pool[Math.floor(Math.random() * entry.pool.length)];
}

export const audioService = new AudioService();
